import React, { useState, useCallback, useEffect, useRef } from 'react';
import ConfirmModal from '../common/ConfirmModal';
import Button from '../common/Button';
import Sheet from '../common/Sheet';

// Below desktop (all mobile + all tablet, < 1280) → confirms render as a bottom sheet (thumb zone),
// matching the app's sheet-below-1280 convention; desktop (>= 1280) keeps the centered ConfirmModal.
// Inlined (not imported) to avoid a circular import (setupCommon imports askConfirm from here).
const isBelowDesktop = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 1279px)').matches;

/**
 * confirmService — an imperative, promise-returning confirm for the Setup subsystem, so the
 * discard / delete / reset guards render the house ConfirmModal instead of a native window.confirm
 * and the whole app stays cohesive.
 *
 *   await askConfirm({ title, message, confirmLabel, cancelLabel, danger })  // → true / false
 *
 * Mount <ConfirmHost/> ONCE (SetupView) so every setup screen shares one dialog. If no host is
 * mounted — e.g. a unit test rendering a screen in isolation — askConfirm falls back to the native
 * window.confirm, so existing tests keep passing without wiring a provider.
 */
let _handler = null;

export const askConfirm = (opts = {}) => {
  if (_handler) return _handler(opts);
  // eslint-disable-next-line no-restricted-globals, no-alert
  const ok = (typeof window !== 'undefined' && window.confirm) ? window.confirm(opts.message || '') : true;
  return Promise.resolve(ok);
};

export const ConfirmHost = ({ eviewMode }) => {
  const [req, setReq] = useState(null);   // { opts, resolve } for the pending ask
  const reqRef = useRef(null);
  reqRef.current = req;
  // If a confirm is already pending when a new one is asked, settle the stale one as cancelled first —
  // otherwise its awaiter (e.g. a navigation guard) would hang forever (review #2).
  const ask = useCallback((opts) => new Promise((resolve) => {
    setReq((prev) => { if (prev) prev.resolve(false); return { opts: opts || {}, resolve }; });
  }), []);
  useEffect(() => {
    _handler = ask;
    return () => { if (_handler === ask) _handler = null; };
  }, [ask]);
  // On unmount (leaving Setup with a confirm still open), settle any outstanding confirm as cancelled so
  // its awaiter never hangs (review #3).
  useEffect(() => () => { if (reqRef.current) reqRef.current.resolve(false); }, []);

  const finish = (val) => {
    if (req) req.resolve(val);
    setReq(null);
  };

  const o = req ? req.opts : {};
  // Mobile + tablet (< 1280): a bottom sheet (matches the app's other sheets, thumb-reachable). Desktop
  // keeps the centered ConfirmModal. The scrim / ✕ / Esc all resolve to Cancel (never a destructive default).
  if (req && isBelowDesktop()) {
    return (
      <Sheet className={'app-sheet setup-confirm-sheet' + (eviewMode ? ' eview-mode' : '')}
        title={o.title || 'Confirm'} role="alertdialog" onClose={() => finish(false)}>
        {o.message ? <p className="setup-confirm-sheet__message">{o.message}</p> : null}
        {/* No explicit Cancel — the ✕, the scrim (tap-outside) and Esc all resolve to cancel, so a
            Cancel button is redundant on the sheet (David 2026-08-20). Just the primary action. */}
        <div className="setup-confirm-sheet__actions">
          <Button onClick={() => finish(true)} buttonText={o.confirmLabel || 'OK'}
            className={o.danger ? 'modal-btn--danger' : 'modal-btn--primary'} />
        </div>
      </Sheet>
    );
  }
  return (
    <ConfirmModal
      show={!!req}
      title={o.title}
      message={o.message}
      confirmLabel={o.confirmLabel}
      cancelLabel={o.cancelLabel}
      danger={o.danger}
      eviewMode={eviewMode}
      onConfirm={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );
};
